<template>
  <div class="matching-list-interests-targets">
    <div class="matching-list__cards-header">
      <h3 class="matching-list__cards-headline">
        {{ matchCountTitle }}
      </h3>
      <!-- <MatchingFilters class="matching-list__cards-filters" /> -->
    </div>
    <div
      v-if="isInterestLoading"
      v-loading="isInterestLoading"
      class="matching-list__loading"
    />
    <VirtualGrid
      v-else-if="hasMatchScores"
      :columns="virtualGridColumns"
      :grid-gap="$options.static.virtualGridGap"
      :item-height="$options.static.virtualRowHeight"
      :items="matchCards"
      :load-on-mount="true"
      @scroll="fetchMoreInterests"
    >
      <template #items="items">
        <MatchingCard
          v-if="items.item"
          :key="`matching-card--${items.item.index}`"
          :about="items.item.about"
          :affiliates="items.item.affiliates"
          :badges="items.item.badges"
          :company-id="items.item.id"
          :company-uid="items.item.uid"
          :level="items.item.level"
          :location="items.item.location"
          :locations="items.item.locations"
          :match-percentage="items.item.percentage"
          :name="items.item.name"
          :offerings="items.item.offerings"
          :sectors="items.item.sectors"
          :show-list-button="showListFeature"
          :thumbnail="items.item.thumbnail"
          @click="clickMatchingCardHandler(items.item.id)"
        />
      </template>
      <template #loading="loading">
        <MatchingCardPlaceholder
          :key="`virtual-list-placeholder-${loading.item}`"
        />
      </template>
    </VirtualGrid>
    <MatchingEmptyResults
      v-else
      :notice="false"
      icon="matching.matchingInterest.matchingList.meEmptyState.icon"
      subtitle="matching.matchingInterest.matchingList.meEmptyState.subtitle"
      title="matching.matchingInterest.matchingList.meEmptyState.title"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import MatchingCard from "@/components/matching-card/matching-card.vue";
import MatchingEmptyResults from "@/modules/matching/components/matching-empty-results/matching-empty-results.vue";
import MatchingFilters from "@/modules/matching/components/matching-filters/matching-filters.vue";
import MatchingListMixin from "@/mixins/matching-list.mixin";
import { IMatchingScore } from "@/modules/matching/services/data/matching-score/matching-score.interface";
import VirtualGrid, {
  IStatusChanger,
} from "@/modules/matching/components/virtual-grid/virtual-grid.vue";
import { EMatchingInterestActions } from "@/modules/matching/services/store/matching-interest/matching-interest.types";
import MatchingCardPlaceholder from "@/components/matching-card/matching-card-placeholder.vue";

export default defineComponent({
  name: "MatchingListInterestsTargets",

  components: {
    MatchingCard,
    MatchingEmptyResults,
    MatchingFilters,
    VirtualGrid,
    MatchingCardPlaceholder,
  },

  mixins: [MatchingListMixin],

  static: {
    virtualRowHeight: 276,
    virtualGridGap: 40,
  },

  computed: {
    matchScores(): IMatchingScore[] {
      return this.$store.state.matchingInterest.targetMatchScores;
    },

    nextPage(): number | null {
      return this.$store.state.matchingInterest.targetMatchScoresPage;
    },

    matchCount(): number {
      return this.$store.state.matchingInterest.targetMatchScoresCount;
    },

    hasMatchScores(): boolean {
      return !!this.matchScores.length;
    },

    matchCountTitle(): string {
      let title = "";
      const hasOnlyOneMatch = this.matchCount === 1;

      if (hasOnlyOneMatch) {
        title = `1 ${this.$t(`common.userTypes[${this.targetUserType}]`)}`;
      } else {
        title = `${this.matchCount} ${this.$t(
          `common.userTypesPlural[${this.targetUserType}]`,
        )}`;
      }

      return title;
    },

    virtualGridColumns(): number {
      if (this.$screen.xlUp) {
        return 4;
      }

      if (this.$screen.lgUp) {
        return 3;
      }

      if (this.$screen.mdUp) {
        return 2;
      }

      return 1;
    },
  },

  methods: {
    async fetchMoreInterests($status: IStatusChanger) {
      if (!this.nextPage) {
        $status.complete();
        return;
      }

      await this.$store.dispatch(EMatchingInterestActions.FETCH_TARGET_SCORES, {
        page: this.nextPage,
        ...this.globalFilters,
      });

      !this.nextPage ? $status.complete() : $status.ready();
    },
  },
});
</script>

<style lang="scss" scoped>
.matching-list__cards-grid {
  position: relative;
  top: 0;
  left: 0;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto;
  grid-row-gap: 50px;
  grid-column-gap: 24px;
  justify-content: center;
  justify-items: center;

  @include breakpoint-up(md) {
    grid-template-columns: repeat(2, auto);
    grid-row-gap: 24px;
    justify-content: left;
    padding-top: 15px;
  }

  @include breakpoint-up(lg) {
    grid-template-columns: repeat(3, auto);
  }

  @include breakpoint-up(xl) {
    grid-template-columns: repeat(4, auto);
  }
}

.matching-empty-results {
  padding-top: 30px;
  margin: 0 auto;
}

.matching-empty-results :deep() .px-notice {
  margin: 16px auto 0;

  @include breakpoint-up(md) {
    max-width: 512px;
    margin-top: 26px;
  }
}

.matching-list__loading {
  height: 200px;

  @include breakpoint-up(md) {
    top: 1px;
    height: 290px;
  }
}

.matching-list__cards-header {
  position: relative;
  display: flex;
  flex-flow: row wrap;
  padding-top: 4px;
  padding-bottom: 4px;

  @include breakpoint-up(md) {
    top: 26px;
    flex-direction: column;
    padding: 12px 0 0;
  }
}

.matching-list__cards-headline {
  padding-top: 12px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;

  @include breakpoint-up(md) {
    padding-top: 0;
    font-size: 20px;
    line-height: 25px;
  }

  &:only-child {
    @include breakpoint-down(md) {
      width: 290px;
    }
  }

  &:not(:only-child) {
    @include breakpoint-down(md) {
      flex: 1 1 40%;
      padding-right: 30px;
    }
  }
}
</style>
